import React, { useEffect, useState } from "react";
import { cn } from "../../../lib/utils";
import {
  Button,
  Card,
  CardTitle,
  FormField,
  Input,
  Select,
  SectionHeader,
} from "../../../shared/ui/composites";
import { useUpdateSettingJsonMutation } from "../api/react-query";
import {
  RETURN_QUERY_OPTIONS,
  COMMISSION_COMPONENTS,
  INDICATOR_TABLE_ORDER,
  ENTRY_GATED_KEYS,
  GATED_COMPONENT_LABELS,
} from "../../staff/utils/commissionConfig";
import BandTable from "./BandTable";

function orderedTableKeys(tables) {
  const known = INDICATOR_TABLE_ORDER.filter((key) => tables[key]);
  const extra = Object.keys(tables).filter((key) => !INDICATOR_TABLE_ORDER.includes(key));
  return known.length || extra.length ? [...known, ...extra] : INDICATOR_TABLE_ORDER;
}

function localizeIndex(index) {
  return ["۱.", "۲.", "۳.", "۴.", "۵.", "۶."][index] || `${index + 1}.`;
}

export default function CommissionIndicatorsEditor({ setting }) {
  const updateMutation = useUpdateSettingJsonMutation();
  const [model, setModel] = useState(setting?.settings_json || {});
  const [message, setMessage] = useState("");

  useEffect(() => {
    setModel(setting?.settings_json || {});
  }, [setting]);

  const tables = model.tables || {};
  const queries = model.queries || {};
  const entry = model.entry_condition || { min_percent: 70 };

  const setTable = (key, table) => {
    setModel((prev) => ({
      ...prev,
      tables: { ...(prev.tables || {}), [key]: table },
    }));
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        settingsKey: setting.settings_key,
        settings_json: model,
      });
      setMessage("ذخیره شد");
    } catch (err) {
      setMessage(err?.response?.data?.error || err.message || "خطا در ذخیره");
    }
  };

  return (
    <div className="space-y-4">
      <SectionHeader
        title="شاخص‌های محاسبه پورسانت"
        description="در این بخش مقادیر و درصدهای محاسبه پورسانت را بر اساس شاخص‌های مختلف تنظیم کنید."
      />

      <div className="grid gap-4 xl:grid-cols-2">
        {orderedTableKeys(tables).map((tableKey, index) => {
          const component = COMMISSION_COMPONENTS.find((c) => c.tableKey === tableKey);
          const fallbackTitle = component?.tableTitle
            ? `${localizeIndex(index)} ${component.tableTitle}`
            : `${localizeIndex(index)} نحوه محاسبه ${component?.fa || tableKey}`;
          const table = tables[tableKey] || { key: tableKey, title: fallbackTitle, bands: [] };
          const showQueryPicker = Boolean(component?.queryPicker);
          return (
            <BandTable
              key={tableKey}
              table={{ ...table, title: table.title || fallbackTitle }}
              onChange={(next) => setTable(tableKey, next)}
              extraHeader={
                showQueryPicker ? (
                  <div className="px-3 pb-2">
                    <FormField label="کوئری درصد مرجوعی" htmlFor="return-query">
                      <Select
                        id="return-query"
                        value={queries[tableKey] || table.query_key || "return_percentage_gross_carton"}
                        onChange={(e) => {
                          const query_key = e.target.value;
                          setTable(tableKey, { ...table, query_key });
                          setModel((prev) => ({
                            ...prev,
                            queries: { ...(prev.queries || {}), [tableKey]: query_key },
                          }));
                        }}
                      >
                        {RETURN_QUERY_OPTIONS.map((opt) => (
                          <option key={opt.key} value={opt.key}>{opt.fa}</option>
                        ))}
                      </Select>
                    </FormField>
                  </div>
                ) : null
              }
            />
          );
        })}
      </div>

      <Card className="border-sky-200 bg-sky-50/70 p-4">
        <CardTitle className="mb-2 text-sm text-sky-900">شرط ورود</CardTitle>
        <p className="mb-3 text-xs text-sky-800">
          برای محاسبه شاخص‌های {GATED_COMPONENT_LABELS} این شرط اعمال می‌شود.
          شاخص تحقق تارگت حجمی مشمول شرط ورود نیست.
        </p>
        <FormField
          label="درصد تحقق تارگت فروش کل سبد (شرط ورود)"
          htmlFor="entry-min"
        >
          <label className="flex max-w-xs items-center gap-2">
            <Input
              id="entry-min"
              type="number"
              min="0"
              max="100"
              variant="form"
              step="0.01"
              value={entry.min_percent ?? 70}
              onChange={(e) =>
                setModel((prev) => ({
                  ...prev,
                  entry_condition: {
                    ...(prev.entry_condition || {}),
                    min_percent: Number(e.target.value),
                    applies_to: ENTRY_GATED_KEYS,
                  },
                }))
              }
            />
            <span>%</span>
          </label>
        </FormField>
      </Card>

      <Card className="border-amber-200 bg-amber-50/70 p-4">
        <CardTitle className="mb-2 text-sm text-amber-900">سقف ریالی پورسانت حجمی</CardTitle>
        <p className="mb-3 text-xs text-amber-800">
          اگر پورسانت باند گروه از این آستانه نسبت به فروش ریالی همان گروه بیشتر شود، به‌جای باند، درصد سقف از فروش ریالی پرداخت می‌شود.
          پیش‌فرض اکسل: اگر باند / ریال &gt; ۲٫۲٪ آنگاه ۲٪ فروش.
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          <FormField label="آستانه (٪ از فروش ریالی گروه)" htmlFor="rial-cap-trigger">
            <label className="flex max-w-xs items-center gap-2">
              <Input
                id="rial-cap-trigger"
                type="number"
                min="0"
                step="0.01"
                variant="form"
                value={model.rial_cap_trigger_percent ?? 2.2}
                onChange={(e) =>
                  setModel((prev) => ({
                    ...prev,
                    rial_cap_trigger_percent: Number(e.target.value),
                  }))
                }
              />
              <span>%</span>
            </label>
          </FormField>
          <FormField label="سقف پرداخت (٪ از فروش ریالی گروه)" htmlFor="rial-cap-percent">
            <label className="flex max-w-xs items-center gap-2">
              <Input
                id="rial-cap-percent"
                type="number"
                min="0"
                step="0.01"
                variant="form"
                value={model.rial_cap_percent ?? 2}
                onChange={(e) =>
                  setModel((prev) => ({
                    ...prev,
                    rial_cap_percent: Number(e.target.value),
                  }))
                }
              />
              <span>%</span>
            </label>
          </FormField>
        </div>
      </Card>

      <div className="flex items-center gap-3">
        <Button type="button" variant="submit" onClick={handleSave} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? "در حال ذخیره..." : "ذخیره شاخص‌ها"}
        </Button>
        {message ? (
          <span className={cn("text-sm", message === "ذخیره شد" ? "text-emerald-700" : "text-red-600")}>
            {message}
          </span>
        ) : null}
      </div>
    </div>
  );
}
